import { useEffect, useState } from 'react'
import { useRoutingStore } from '../stores/routingStore'
import { MainMenuSection } from '../components/MainMenu/types/MainMenu.types'

// Dialog routing hook for managing dialog state based on URL routes
export const useDialogRouting = () => {
  const routingStore = useRoutingStore()
  const [dialogState, setDialogState] = useState<{
    [key: string]: boolean
  }>({})

  // Dialog route mappings
  const DIALOG_ROUTES: Record<string, string> = {
    'companyDialog': 'company',
    'inviteUserDialog': 'inviteUser',
    'editUserDialog': 'editUser',
    'usersDialog': 'users',
    'companyRoomsDialog': 'companyRooms',
  }

  // Check if current route is a dialog route
  const isDialogRoute = (section: MainMenuSection): boolean => {
    return Object.keys(DIALOG_ROUTES).includes(section)
  }

  // Get dialog name from section
  const getDialogName = (section: MainMenuSection): string | null => {
    return DIALOG_ROUTES[section] || null
  }

  // Open dialog via routing
  const openDialog = (dialogName: string) => {
    const section = Object.keys(DIALOG_ROUTES).find(
      key => DIALOG_ROUTES[key] === dialogName
    ) as MainMenuSection
    
    if (section) {
      routingStore.navigateToSection(section)
    }
  }

  // Close dialog and navigate back
  const closeDialog = (fallbackSection: MainMenuSection = 'companyManagement') => {
    routingStore.navigateToSection(fallbackSection)
  }

  // Check if a specific dialog is open
  const isDialogOpen = (dialogName: string): boolean => {
    const section = Object.keys(DIALOG_ROUTES).find(
      key => DIALOG_ROUTES[key] === dialogName
    ) as MainMenuSection
    
    return routingStore.currentSection === section
  }

  // Get current dialog name
  const getCurrentDialog = (): string | null => {
    if (isDialogRoute(routingStore.currentSection)) {
      return getDialogName(routingStore.currentSection)
    }
    return null
  }

  // Update dialog state based on current route
  useEffect(() => {
    const currentSection = routingStore.currentSection
    const newDialogState: { [key: string]: boolean } = {}
    
    // Reset all dialogs
    Object.values(DIALOG_ROUTES).forEach(dialogName => {
      newDialogState[dialogName] = false
    })
    
    // Set current dialog as open
    if (isDialogRoute(currentSection)) {
      const dialogName = getDialogName(currentSection)
      if (dialogName) {
        newDialogState[dialogName] = true
      }
    }
    
    setDialogState(newDialogState)
  }, [routingStore.currentSection])

  return {
    dialogState,
    isDialogRoute,
    getDialogName,
    openDialog,
    closeDialog,
    isDialogOpen,
    getCurrentDialog,
  }
}

export default useDialogRouting
